import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_LANE,
  DEFAULT_PRICING_REGION,
  DEEP_UNDERSTANDING,
  DOMAINS,
  GLOBAL_LANGUAGE,
  LANG_TO_LANE,
  LANGUAGES,
  PERSONAL_GUIDANCE,
  PRIVACY,
  REGION_PRICES,
  UNIQUE_MAP,
  type LandingLangCode,
} from "./landingContent";
import {
  createLandingCopy,
  getInitialLanguage,
  LandingCopyProvider,
} from "./landingCopy";
import LandingFooter from "./LandingFooter";
import LandingNav from "./LandingNav";
import PlansSection from "./PlansSection";
import ProfileForm, { type ProfileValues } from "./ProfileForm";
import StarField from "./StarField";
import { useScrollReveal } from "./useScrollReveal";
import {
  CalmGuidanceSection,
  FaqSection,
  FeatureGridSection,
  FinalCtaSection,
  GlobalReach,
  HeroSection,
  LaneDemo,
  LifeNavigationSection,
  MetricsSection,
  StepsSection,
} from "./LandingSections";
import "./astriaLanding.css";

/** Symbols for the "global by design" cards (the others use planet glyphs). */
const GLOBAL_GLYPHS = ["◐", "❋", "☉"];

interface LandingPrefs {
  lang: LandingLangCode;
  /** Tone lane previewed in the lane demo. */
  lane: string;
  /** Country whose Pro prices are shown. */
  region: string;
}

/** Applies a language choice: its tone lane and pricing country follow when it has one. */
const prefsForLanguage = (lang: LandingLangCode, current?: LandingPrefs): LandingPrefs => {
  const defaultRegion = LANGUAGES.find((language) => language.code === lang)?.defaultRegion;
  const hasPrice = REGION_PRICES.some((price) => price.id === defaultRegion);
  return {
    lang,
    lane: LANG_TO_LANE[lang] ?? current?.lane ?? DEFAULT_LANE,
    region: (hasPrice && defaultRegion) || current?.region || DEFAULT_PRICING_REGION,
  };
};

const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Marketing landing page shown to signed-out visitors on the Astria domain.
 * Renders full-screen with its own navigation, so it replaces the app shell.
 */
const AstriaLanding: React.FC = () => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [prefs, setPrefs] = useState<LandingPrefs>(() =>
    prefsForLanguage(getInitialLanguage()),
  );
  const [scrolled, setScrolled] = useState(false);
  useScrollReveal(rootRef);

  const copy = useMemo(() => createLandingCopy(prefs.lang), [prefs.lang]);
  const direction = LANGUAGES.find((language) => language.code === prefs.lang)?.dir ?? "ltr";

  const jumpToSection = useCallback((id: string) => {
    const behavior = prefersReducedMotion() ? "auto" : "smooth";
    if (id === "top") {
      rootRef.current?.scrollTo({ top: 0, behavior });
      return;
    }
    rootRef.current
      ?.querySelector(`#${id}`)
      ?.scrollIntoView({ behavior, block: "start" });
  }, []);

  const handleAction = useCallback(
    (action: string) => {
      if (action === "learn_more") {
        jumpToSection("steps_to_clarity");
        return;
      }
      // Every other call to action starts with an account.
      navigate("/register");
    },
    [jumpToSection, navigate],
  );

  const handleProfileComplete = useCallback(
    (profile: ProfileValues) => {
      navigate("/register", { state: { prefillUsername: profile.name } });
    },
    [navigate],
  );

  return (
    <LandingCopyProvider value={copy}>
      <div
        ref={rootRef}
        className="astria-landing"
        lang={prefs.lang}
        dir={direction}
        onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 30)}
      >
        <StarField />
        <div className="al-glow al-glow--primary" aria-hidden="true" />
        <div className="al-glow al-glow--secondary" aria-hidden="true" />

        <LandingNav
          scrolled={scrolled}
          onJumpToSection={jumpToSection}
          onLanguageChange={(code) => setPrefs((current) => prefsForLanguage(code, current))}
          onLogin={() => navigate("/login")}
          onGetMap={() => handleAction("start_map")}
        />

        <main id="top">
          <HeroSection onAction={handleAction} />
          <FeatureGridSection id="unique_map" titleKey="uniqueT" content={UNIQUE_MAP} />
          <FeatureGridSection
            id="personal_guidance"
            titleKey="fitT"
            content={PERSONAL_GUIDANCE}
            glyphOffset={3}
          >
            <LaneDemo
              lane={prefs.lane}
              onLaneChange={(lane) => setPrefs((current) => ({ ...current, lane }))}
            />
          </FeatureGridSection>
          <LifeNavigationSection />
          <CalmGuidanceSection />
          <MetricsSection />
          <StepsSection />
          <ProfileForm onComplete={handleProfileComplete} />
          <FeatureGridSection
            id="deep_understanding"
            content={DEEP_UNDERSTANDING}
            glyphOffset={5}
          />
          <FeatureGridSection id="domains" titleKey="domainsT" content={DOMAINS} glyphOffset={1} />
          <FeatureGridSection
            id="global"
            titleKey="globalT"
            content={GLOBAL_LANGUAGE}
            glyphs={GLOBAL_GLYPHS}
          >
            <GlobalReach />
          </FeatureGridSection>
          <FeatureGridSection id="privacy" titleKey="privacyT" content={PRIVACY} glyphOffset={6} />
          <PlansSection
            regionId={prefs.region}
            onRegionChange={(region) => setPrefs((current) => ({ ...current, region }))}
            onAction={handleAction}
          />
          <FaqSection />
          <FinalCtaSection onAction={handleAction} />
        </main>

        <LandingFooter />
      </div>
    </LandingCopyProvider>
  );
};

export default AstriaLanding;
